import { useEffect } from "react";
import { useNavigate } from "react-router";

import type { ConnectedProjectDto } from "@/api/types";
import { ErrorPage } from "@/components/Error/ErrorPage";
import { canAccessHome } from "@/components/ProjectSidebar/permissions";
import { hasAdminProject } from "@/helpers/portfolio-access";
import { useLinkedProjects } from "@/hooks/Network/useLinkedProjects";
import { usePermission } from "@/hooks/usePermission";
import { routes } from "@/routes";

export function PortfolioRedirect(): React.ReactNode {
  const { data: linkedProjects, error } = useLinkedProjects();
  const navigate = useNavigate();
  const hasPermission = usePermission();

  useEffect(() => {
    if (!linkedProjects || linkedProjects.length === 0) {
      return;
    }

    const target = getPortfolioRedirectTarget(linkedProjects, hasPermission(canAccessHome));
    if (target) {
      void navigate(target, { replace: true });
    }
  }, [hasPermission, navigate, linkedProjects]);

  if (error || linkedProjects?.length === 0) {
    return <ErrorPage error={error} />;
  }

  return null;
}

export function getPortfolioRedirectTarget(
  linkedProjects: readonly Pick<ConnectedProjectDto, "slug" | "userRole">[],
  canAccessProjectHome: boolean,
): string | undefined {
  if (hasAdminProject(linkedProjects)) {
    return routes.portfolio.overview();
  }

  const fallbackProject = linkedProjects[0];
  if (!fallbackProject) {
    return undefined;
  }

  return canAccessProjectHome
    ? routes.home.home({ slug: fallbackProject.slug })
    : routes.communityFeed.list({ slug: fallbackProject.slug });
}
